import { useEffect, useState } from 'react';
import { API } from '../../api';
import { formatDate, fromEpochSeconds } from '../../utils/formatDate';
import { useProgramContext } from '../../hooks/useProgramContext';
import { MedalKey, MedalSection, parseTierHistory, TierHistoryPoint } from './scorecardData';
import { TopPerformersData, ProgramDueItem } from './gamificationTypes';

// Live data for the learner gamification bar — wraps
// GET /gamification/learner/summary[?grpId=] and adapts the BE payload to
// the shapes the components render.
//
// Real data or nothing: this returns the adapted summary or `null`, never a
// placeholder, and every consumer treats `null` as "hide this piece". The
// dummy fallbacks it used to allow reached real learners when the dashboard
// went GA (RDP, Sep 2026).

const getAuthHeaders = () => {
  try {
    const raw = localStorage.getItem('tokens');
    const tokens = raw ? JSON.parse(raw) : {};
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '' };
  } catch {
    return { idtoken: '', authorization: '' };
  }
};

export interface LearnerGamificationSummary {
  streak: { weeksActive: number; lessonsThisWeek: number };
  medals: { overall: MedalKey; sections: MedalSection[]; history?: TierHistoryPoint[] };
  topPerformers: TopPerformersData;
  programsDue: ProgramDueItem[];
  runningScore: number | null;
}

// Section metadata the BE doesn't repeat (measure line + band boundaries).
const SECTION_MEASURES: Record<string, string> = {
  lessonCompletion: '% of your lessons completed',
  knowledgeRetention: 'Your score across all lesson quiz assessments',
  knowledgeMastery: 'Your pass rate across all lesson live scenarios with Elliott, retakes included',
  learningRange: 'The % of your programs that you have started',
  proactiveLearning: 'How recently you last messaged Elliott in Conversations'
};
const PCT_BOUNDARIES: [number, number, number] = [25, 50, 75];

// Proactive Learning measures recency, so the BE sends a band POSITION on the
// same 0–100 track instead of a percentage. The bar renders identically; only
// the labels change, since "0–24%" means nothing on a recency axis.
const RECENCY_BAND_LABELS = [
  'No message in 30 days',
  '15–30 days ago',
  '8–14 days ago',
  'Within 7 days',
  '3+ messages in 7 days'
];
const RECENCY_GATE_LABELS = ['30d', '14d', '7d', '3 msgs'];

const adaptMedalSection = (s: any): MedalSection => {
  const isRecency = s?.key === 'proactiveLearning';
  return {
    name: s?.name || '',
    measure: SECTION_MEASURES[s?.key] || '',
    pos: Math.min(typeof s?.pct === 'number' ? s.pct : 0, 100),
    value: s?.value || '',
    // Null: nothing it measures is required of them (DA-5056 R5); shown "-", never as Rust.
    medal: s?.medal === null ? null : ((s?.medal || 'wood') as MedalKey),
    boundaries: PCT_BOUNDARIES,
    ...(isRecency
      ? { bandLabels: RECENCY_BAND_LABELS, gateLabels: RECENCY_GATE_LABELS }
      : {})
  };
};

// A BE medals payload ({ overall, sections }) in the panel's shape. Exported for
// Org Members, whose list payload carries each member's medals in the same form
// this summary does.
export const adaptMedals = (medals: any): LearnerGamificationSummary['medals'] => ({
  overall: (medals?.overall || 'wood') as MedalKey,
  sections: (medals?.sections || []).map(adaptMedalSection),
  // Weekly snapshots of the overall tier for the 6 month view. Empty until the
  // BE saves them; the view plots this week live and earlier weeks as Rust.
  history: parseTierHistory(medals?.history)
});

const adaptSummary = (data: any): LearnerGamificationSummary | null => {
  if (!data || !data.streak || !data.medals || !data.topPerformers) return null;
  return {
    streak: {
      weeksActive: data.streak.weeksActive ?? 0,
      lessonsThisWeek: data.streak.lessonsThisWeek ?? 0
    },
    medals: adaptMedals(data.medals),
    topPerformers: data.topPerformers as TopPerformersData,
    programsDue: (data.programsDue || []).map(
      (p: any): ProgramDueItem => ({
        programName: p.programName || '',
        due: formatDate(fromEpochSeconds(p.dueBy)),
        dueBy: typeof p.dueBy === 'number' ? p.dueBy : null,
        isOverdue: Boolean(p.isOverdue)
      })
    ),
    runningScore: typeof data.runningScore === 'number' ? data.runningScore : null
  };
};

export const useLearnerGamification = (
  grpId?: string,
  // `skip` lets a consumer opt out of the fetch when a host page has already
  // made it and is passing the summary down — the endpoint is uncached, so two
  // live hooks on one page means two identical round trips.
  options?: { skip?: boolean }
): { summary: LearnerGamificationSummary | null; loading: boolean } => {
  const { location } = useProgramContext();
  const skip = options?.skip === true;
  const [summary, setSummary] = useState<LearnerGamificationSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchSummary = async () => {
      const { idtoken, authorization } = getAuthHeaders();
      if (!idtoken || !authorization) {
        setLoading(false);
        return;
      }
      try {
        const res = await API.getLearnerGamificationSummary({ grpId, idtoken, authorization, location });
        if (!cancelled) setSummary(adaptSummary(res?.data));
      } catch {
        // Silent — consumers hide every chip rather than invent numbers.
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [grpId, location, skip]);

  return { summary, loading };
};
